'use strict';

var AppState = require('app_state');
var App = require('app');
var Consts = require('consts');
var i18n = require('utils/i18n');
var Notification = require('utils/notification');

var SmartTempState = Backbone.Model.extend({
    url: function () {
        return '/api/v1/smart-temp/' + this.get('meter_id') + '/' + this.get('controller_id');
    },

    validate: function () {
        if (this.get('meter_id') && this.get('controller_id') && !(this.get('active') && this.get('dummy_values'))) {
            return undefined; // 'undefined' indicates no validation error
        }
        return 'invalid smart temp state';
    },
});

var backendMapping = {
    'control-1': 'HC1',
    'control-2': 'HC2',
    'control-3': 'HC3',
    'control-water': 'DHW',
};

var SmartTempInfo = Backbone.Collection.extend({
    model: SmartTempState,

    initialize: function (baseDeviceID) {
        this.baseDeviceID = baseDeviceID;
    },

    url: function () {
        return '/api/v1/smart-temp?id=' + this.baseDeviceID;
    },

    getMeterIDs: function () {
        return this.pluck('meter_id');
    },

    getActiveModels: function (dryRunOk) {
        return this.filter(function (model) {
            return model.get('active') || (dryRunOk && model.get('dummy_values'));
        });
    },

    getActiveMeterIDs: function (dryRunOk) {
        var ids = [];
        this.each(function (model) {
            if (model.get('active') || (dryRunOk && model.get('dummy_values'))) {
                ids.push(model.get('meter_id'));
            }
        });
        return ids;
    },

    isActive: function () {
        return this.where({ active: true }).length > 0;
    },

    isDryRun: function () {
        var hasDummyValues = this.where({ dummy_values: true }).length > 0;
        return hasDummyValues && !this.isActive();
    },

    isInactive: function () {
        return this.models.length === 0 || !(this.isActive() || this.isDryRun());
    },

    findModelWithMatchingHCs: function (meterID, meterHCs) {
        var self = this;
        var res = undefined;

        AppState.requireCompanyFeatures(function () {
            if (AppState.Cache.companyFeatures[Consts.Features.SmartTemp] === true) {
                // check if there is a model for which all the HCs match
                res = _.find(self.models, function (model) {
                    if (model.get('meter_id') !== meterID) {
                        return false;
                    }
                    var requiredHCs = model.get('meter_position');
                    if (meterHCs.length !== requiredHCs.length) {
                        return false;
                    }
                    return meterHCs.every(function (hc) {
                        return requiredHCs.includes(backendMapping[hc]);
                    });
                });
            }
        });
        return res;
    },

    getOptimizedRegisters: function (dryRunOk) {
        var registers = [];
        _.each(this.getActiveModels(dryRunOk), function (model) {
            registers.push(model.get('register_to_optimize'));
        });
        return registers;
    },

    getOptimizedHCs: function (dryRunOk) {
        var hcs = [];
        _.each(this.getActiveModels(dryRunOk), function (model) {
            var tmpHCs = model.get('meter_position');
            _.each(tmpHCs, function (hc) {
                if (!hcs.includes(hc)) {
                    hcs.push(hc);
                }
            });
        });
        return hcs;
    },

    getProtectedValues: function (dryRunOk) {
        var values = [];
        var activeModels = this.getActiveModels(dryRunOk);
        _.each(this.getActiveModels(dryRunOk), function (model) {
            var protectedValues = model.get('protected_values');
            _.each(protectedValues, function (val) {
                if (!values.includes(val)) {
                    values.push(val);
                }
            });
        });
        if (activeModels.length > 0) {
            values.push('MBR_6514');
        }
        return values;
    },

    activate: function (meterID, circuits, isDryRun, options) {
        var model = this.findModelWithMatchingHCs(meterID, circuits);
        model.set('dummy_values', isDryRun);
        model.set('active', !isDryRun);
        options = options || {};
        options.type = 'PUT';
        model.save(null, options);
    },

    deactivate: function (meterID, options) {
        var model = this.find(function (m) {
            return m.get('meter_id') === meterID && (m.get('active') || m.get('dummy_values'));
        });
        if (!model) return;
        model.set('dummy_values', false);
        model.set('active', false);
        if (!options) options = {};
        options.type = 'PUT';
        model.save(null, options);
    },

    isOptimizingSetpoint: function () {
        var registers = this.getOptimizedRegisters(false);
        return (
            registers.length > 0 &&
            !registers.every(function (reg) {
                return reg === 'MBR_10';
            })
        );
    },

    isOptimizingSensor: function () {
        var registers = this.getOptimizedRegisters(false);
        return registers.length > 0 && registers.includes('MBR_10');
    },

    getWarnings: function (infoMsg) {
        if (infoMsg && (this.isActive() || this.isDryRun())) {
            return [
                {
                    title: this.isActive()
                        ? i18n.TR('Smart Temp aktiv') + ':'
                        : i18n.TR('Smart Temp-Vorschau aktiv') + ':',
                    info: infoMsg,
                },
            ];
        }

        if (!this.isActive()) {
            return [];
        }

        var hints = [];
        if (this.isOptimizingSensor()) {
            hints.push(i18n.TR('Optimierter Außenfühlerwert wird vom Server regelmäßig an das Gerät übertragen.'));
        }
        if (this.isOptimizingSetpoint()) {
            hints.push(i18n.TR('Optimierter Sollwert wird vom Server regelmäßig an das Gerät übertragen.'));
        }
        return [
            {
                title: i18n.TR('Smart Temp aktiv') + ':',
                info: hints.join(' '),
            },
        ];
    },
});

var API = {
    getSmartTempInfoReq: function (deviceID, showErrorMsg) {
        var smartTempInfo = new SmartTempInfo(deviceID);
        var defer = $.Deferred();
        AppState.requireCompanyFeatures(function () {
            if (AppState.Cache.companyFeatures[Consts.Features.SmartTemp]) {
                smartTempInfo.fetch({
                    success: function (data) {
                        defer.resolve(data);
                    },
                    error: function (data) {
                        if (showErrorMsg) {
                            Notification.show(
                                'error',
                                i18n.TR('Fehler'),
                                i18n.TR('Daten für Smart Temp konnten nicht geladen werden.')
                            );
                        }
                        defer.resolve(undefined);
                    },
                });
            } else {
                defer.resolve(smartTempInfo); // no actual fetch being executed
            }
        });
        return defer.promise();
    },
};

App.reqres.setHandler('device:smarttemp', function (deviceID, showErrorMsg) {
    return API.getSmartTempInfoReq(deviceID, showErrorMsg);
});

module.exports = {
    SmartTempInfo: SmartTempInfo,
};
